import { inject, Injectable } from '@angular/core';
import { AuthStoreSelector } from '@common/selectors/authentication.selector';
import { OrderStoreSelector } from '@common/selectors/order.selector';
import { PaymentAtTableStoreSelector } from '@common/selectors/payment-at-table.selector';
import { injectConfirmationPageContext } from '@core/services/content-builder/context-expression-language.util';
import { ExpressionLanguageUtile } from '@core/services/content-builder/expression-language.utile';
import { visibilityRecurrenceMatches } from '@core/services/content-builder/visibility-recurrence/visibility-recurrence.runtime';
import { combineLatest, map } from 'rxjs';

/** Encodes chars that are unsafe in URL query params: @ → %40, + → %20, : → %3a */
function encodeForUrlParam(value: string): string {
  return value.replace(/@/g, '%40').replace(/\+/g, '%20').replace(/:/g, '%3a');
}

function toIsoDate(value: Date | string | null | undefined): string {
  if (value == null) return '';
  const d = typeof value === 'string' ? new Date(value) : value;
  if (isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  const offsetMin = -d.getTimezoneOffset();
  const tzSign = offsetMin >= 0 ? '+' : '-';
  const tz = `${tzSign}${pad(Math.floor(Math.abs(offsetMin) / 60))}:${pad(Math.abs(offsetMin) % 60)}`;
  const iso = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}${tz}`;
  return encodeForUrlParam(iso);
}

@Injectable({ providedIn: 'root' })
export class ContentBuilderVariablesService {
  private readonly paymentAtTableStoreSelector = inject(PaymentAtTableStoreSelector);
  private readonly orderStoreSelector = inject(OrderStoreSelector);
  private readonly authStoreSelector = inject(AuthStoreSelector);

  confirmationPageContext = injectConfirmationPageContext();

  private readonly orderContext$ = combineLatest({
    order: this.orderStoreSelector.selectActive,
    user: this.authStoreSelector.selectUser$,
  }).pipe(
    map(({ order, user }) => ({
      /**
       * Availability: When an order is available (e.g. confirmation page)
       * Content: Client email (on-behalf-of or logged-in user), encoded for URL use (e.g. iframe src)
       * Usage: {{'{{email}}'}}
       */
      email: encodeForUrlParam(order?.on_behalf_of?.email ?? user?.email ?? ''),
      /**
       * Availability: When an order is available (e.g. confirmation page)
       * Content: Client phone number, encoded for URL use (e.g. iframe src)
       * Usage: {{'{{phoneNumber}}'}}
       */
      phoneNumber: encodeForUrlParam(
        order?.on_behalf_of?.phone ??
          order?.contact_phone_number ??
          order?.payment_phone_number ??
          user?.phone ??
          '',
      ),
      /**
       * Availability: When an order is available
       * Content: Order identifier (remote id), encoded for URL use (e.g. iframe src)
       * Usage: {{'{{remoteId}}'}}
       */
      remoteId: encodeForUrlParam(order?.id ?? ''),
      /**
       * Availability: When an order is available
       * Content: Delivery date (wanted_at), ISO 8601 with timezone (e.g. 2025-02-13T13:35:54+01:00)
       * Usage: {{'{{deliveryDate}}'}}
       */
      deliveryDate: toIsoDate(order?.wanted_at ?? null),
      /**
       * Availability: When an order is available
       * Content: Purchase/consumption date (created_at), ISO 8601 with timezone (e.g. 2025-02-13T13:35:54+01:00)
       * Usage: {{'{{experienceAt}}'}}
       */
      experienceAt: order ? toIsoDate((order as { created_at?: Date | string }).created_at) : '',
    })),
  );

  // todo 👇
  // ! please update doc here when updating this service src/app/modules/pages/components/block-field-atoms/shared/content-builder-available-variables.component.ts
  get$() {
    return combineLatest({
      ['PaymentAtTable.onSiteLocationName']: this.paymentAtTableStoreSelector.selectCheck.pipe(
        map(check => check?.onsite_location?.name?.replace(/ /g, '') ?? ''),
      ),
      ...this.confirmationPageContext,
      orderContext: this.orderContext$,
    }).pipe(
      map(data => {
        const timeStamp = new Date().getTime();
        const tableName = data['PaymentAtTable.onSiteLocationName'] ?? '';
        const { orderContext, ...rest } = data;
        return {
          ...rest,
          ...orderContext,
          /**
           * Availability: Always available
           * Content: The current timestamp
           * Example: 1678901234567
           * Usage: {{'{{timeStamp}}'}}
           */
          timeStamp,
          /**
           * Availability: With payment at table option, when a table is selected
           * Content: The table name with spaces removed, with a timestamp
           * Example: "Table 1" => "Table1-1678901234567"
           * Usage: {{'{{Dokaa.PayAtTable.OrderInfo}}'}}
           */
          ['Dokaa.PayAtTable.OrderInfo']: `${String(tableName).replace(/ /g, '')}-${timeStamp}`,
        };
      }),
    );
  }

  parseContentBuilderString$(string: string, context?: Record<string, any>) {
    return this.get$().pipe(
      map(data =>
        string.replace(/{{(.*?)}}/g, (_match, expression: string) => {
          const result = ExpressionLanguageUtile.parseExpressionForTemplate(expression, {
            ...data,
            ...context,
            visibilityRecurrenceMatches,
          });
          return result as string;
        }),
      ),
    );
  }
}
